// Reactive Svelte 5 store using $state runes. All UI components import this directly.
import type { PrinterProfile, SliceSettings, Triangle, GCodeOutput, SliceResult } from '../core/types';
import { BUILTIN_PROFILES } from '../core/printerProfiles';
import { DEFAULT_VASE, type VaseParams, type ProfilePoint } from '../core/vaseModel';
import type { PenPath } from '../core/penTool';
import type { ParamGraph } from '../core/paramGraph';
import { extractMeshProfile } from './meshDeform';
import { detectInitialLang, type Lang } from './i18n';

const DEFAULT_SETTINGS: SliceSettings = {
  layerHeight: 1.0,
  firstLayerHeight: 1.2,
  perimeters: 1,
  infillDensity: 0.0,
  infillPattern: 'lines',
  infillAngle: 45,
  rotateInfillEachLayer: 0,
  scaleInfill: 1.0,
  infillMargin: 0,
  printSpeed: 1500,
  firstLayerSpeed: 800,
  travelSpeed: 3000,
  vaseMode: true,
  bottomLayers: 2,
  topLayers: 0
};

export type AppMode = 'edit' | 'studio' | 'sculpt' | 'slice' | 'preview' | 'param';
export type CameraView = 'iso' | 'top' | 'front' | 'side' | 'persp';
export type RightPanel = 'model' | 'vase' | 'sculpt' | 'templates' | 'gcode';
export type DisplayMode = 'default' | 'shaded' | 'textured' | 'wireframe';
export type EditTool = 'handles' | 'pen' | 'sculpt' | 'crossSection' | 'pattern';
/** What the studio is creating from scratch — controls which tools show. */
export type StudioTarget = 'vase' | 'mesh';

export type SculptBrushMode = 'push' | 'pull' | 'smooth' | 'inflate' | 'pinch';
export type SculptStamp = 'none' | 'star5' | 'star8' | 'gear' | 'ring';

export interface SculptBrush {
  mode: SculptBrushMode;
  radius: number;     // mm
  strength: number;   // mm per stroke step
  falloff: number;    // 0..1 — smoothness of brush edge (0 = hard, 1 = soft)
  symmetryX: boolean; // mirror stroke across the X=0 plane (YZ plane)
  stamp: SculptStamp; // procedural stamp shape modulating falloff
}

export interface AppState {
  lang: Lang;
  theme: 'dark' | 'light';
  triangles: Triangle[];
  fileName: string;
  printer: PrinterProfile;
  settings: SliceSettings;
  vaseParams: VaseParams;
  showVasePanel: boolean;
  showSvgGenerator: boolean;
  /** Rasterization resolution used by svgToTextureGrid for ALL SVG sources
   * (built-in patterns, AI-generated SVGs, vectorized rasters). Higher = sharper
   * displacement on the vase, but more GPU bandwidth. */
  textureResolution: number;
  brush: SculptBrush;
  sliceResult: SliceResult | null;
  gcodeOutput: GCodeOutput | null;
  status: 'idle' | 'loading' | 'slicing' | 'ready';
  errorMessage: string;
  viewMode: 'mesh' | 'layers';
  currentLayerIndex: number;
  mode: AppMode;
  cameraView: CameraView;
  displayMode: DisplayMode;
  /** When false, shading is disabled — flat color everywhere (no shadows). */
  lightOn: boolean;
  /** When false, the facet edge darkening pass is skipped. */
  edgeOn: boolean;
  rightPanel: RightPanel;
  /** When true, the user has opted in to anonymous telemetry. No collection wired yet
   * — this is a settings hook so future telemetry can default to OFF and respect choice. */
  telemetryOptIn: boolean;
  /** True while the user is actively editing the vase profile (handles + wireframe view). */
  vaseEditingProfile: boolean;
  /** Snapshot of vaseParams taken on entry to profile-edit, used by Cancel. */
  vaseEditSnapshot: Partial<VaseParams> | null;
  /** Drag sensitivity 0.05–1.0. Lower = finer control. */
  vaseEditSensitivity: number;
  /** Undo stack of vaseParams snapshots taken on each drag-end. Bounded to 25. */
  vaseEditUndoStack: Partial<VaseParams>[];
  /** Original triangles snapshot when entering profile-edit on a non-vase mesh.
   *  Used to deform from the same starting geometry on every drag (no drift). */
  meshEditOriginal: Triangle[] | null;
  /** Original profile sampled from the mesh at edit-entry. Provides the baseline
   *  ratio for radial scaling. */
  meshEditOriginalProfile: ProfilePoint[] | null;
  /** Current edit profile for non-vase mesh edits. Drag handles mutate this. */
  meshEditProfile: ProfilePoint[] | null;
  /** Undo stack for mesh-mode profile edits. */
  meshEditUndoStack: ProfilePoint[][];
  /** Active editing tool inside Edit mode. 'handles' = legacy on-mesh dots, 'pen' = 2D PenCanvas. */
  editTool: EditTool;
  /** Undo stack for Pen tool edits (full PenPath snapshots, bounded to 25). */
  penEditUndoStack: PenPath[];
  /** Reference photo (data URL) shown as background of PenCanvas. Session-only. */
  refPhotoUrl: string | null;
  /** Reference photo opacity 0..1 — persisted. */
  refPhotoOpacity: number;
  /** Reference photo physical scale (mm per pixel). Calibrated by user. */
  refPhotoScale: number;
  /** Show dimension overlay (height, max diameter, anchor count) on PenCanvas. */
  showDimensions: boolean;
  /** Pen-tool custom pattern path (closed) — used by Phase 3 vector pattern designer.
   *  Lives outside vaseParams because it's a transient editor scratchpad: applying
   *  it bakes a TextureGrid into vaseParams.texture. Session-only. */
  penPattern: PenPath | null;
  /** What the user is creating in Studio mode (vase = revolution-of-profile,
   *  mesh = freeform sketch). Drives which Pen tools are exposed. */
  studioTarget: StudioTarget;
  /** Parametric node graph (Grasshopper-style). Lives in app state so it
   *  persists across mode switches. Output node feeds app.triangles. */
  paramGraph: ParamGraph;
  /** Currently focused parameter (overlay → viewport bridge). Triggers a
   *  bbox highlight + axis arrow in the 3D view so the user sees which part
   *  of the model their slider controls. null when nothing is focused. */
  focusedParam: { nodeId: string; portId: string } | null;
  /** Cylindrical SVG-pattern displacement applied to the param-graph mesh
   *  AFTER eval. Set by the chat tool `set_vase_surface_pattern`. The grid
   *  is rasterized once (async) by svgToTextureGrid and cached here so each
   *  re-eval can apply it synchronously. null = no pattern. */
  paramPattern: { grid: import('../core/vaseModel').TextureGrid; intensity: number; repeatU: number; repeatV: number } | null;
}

function loadSavedSettings(): SliceSettings {
  try {
    const raw = localStorage.getItem('clayforge.settings');
    if (raw) return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return DEFAULT_SETTINGS;
}

function loadSavedPrinter(): PrinterProfile {
  try {
    const id = localStorage.getItem('clayforge.printer');
    if (id) {
      const built = BUILTIN_PROFILES.find((p) => p.id === id);
      if (built) return built;
      const raw = localStorage.getItem('clayforge.customProfiles');
      if (raw) {
        const list = JSON.parse(raw) as PrinterProfile[];
        const custom = list.find((p) => p.id === id);
        if (custom) return custom;
      }
    }
  } catch { /* ignore */ }
  return BUILTIN_PROFILES[0];
}

function loadSavedPrinterId(): string {
  return loadSavedPrinter().id;
}

function loadSavedVase(): VaseParams {
  try {
    const raw = localStorage.getItem('clayforge.vase');
    if (raw) {
      const parsed = JSON.parse(raw);
      // texture is intentionally NOT persisted (large Float32Array, transient)
      return { ...DEFAULT_VASE, ...parsed, texture: null };
    }
  } catch { /* ignore */ }
  return { ...DEFAULT_VASE };
}

function loadSavedBrush(): SculptBrush {
  const fallback: SculptBrush = { mode: 'pull', radius: 8, strength: 0.4, falloff: 0.6, symmetryX: false, stamp: 'none' };
  try {
    const raw = localStorage.getItem('clayforge.brush');
    if (raw) return { ...fallback, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return fallback;
}

function loadSavedNumber(key: string, fallback: number, min: number, max: number): number {
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const v = parseFloat(raw);
      if (isFinite(v)) return Math.max(min, Math.min(max, v));
    }
  } catch { /* ignore */ }
  return fallback;
}

function loadSavedString<T extends string>(key: string, fallback: T, allowed: readonly T[]): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw && (allowed as readonly string[]).includes(raw)) return raw as T;
  } catch { /* ignore */ }
  return fallback;
}

export const app = $state<AppState>({
  lang: detectInitialLang(),
  theme: (localStorage.getItem('clayforge.theme') as 'dark' | 'light' | null) ?? 'dark',
  triangles: [],
  fileName: '',
  printer: loadSavedPrinter(),
  settings: loadSavedSettings(),
  vaseParams: loadSavedVase(),
  showVasePanel: false,
  showSvgGenerator: false,
  textureResolution: loadSavedNumber('clayforge.textureRes', 512, 64, 1024),
  brush: loadSavedBrush(),
  sliceResult: null,
  gcodeOutput: null,
  status: 'idle',
  errorMessage: '',
  viewMode: 'mesh',
  currentLayerIndex: 0,
  mode: 'edit',
  cameraView: loadSavedString('clayforge.cameraView', 'iso', ['iso', 'persp', 'top', 'front', 'side']),
  displayMode: loadSavedString('clayforge.displayMode', 'default', ['default', 'shaded', 'textured', 'wireframe']),
  lightOn: localStorage.getItem('clayforge.lightOn') !== '0',
  edgeOn: localStorage.getItem('clayforge.edgeOn') !== '0',
  rightPanel: 'model',
  telemetryOptIn: localStorage.getItem('clayforge.telemetry') === '1',
  vaseEditingProfile: false,
  vaseEditSnapshot: null,
  vaseEditSensitivity: loadSavedNumber('clayforge.vaseEditSens', 0.5, 0.05, 1.0),
  vaseEditUndoStack: [],
  meshEditOriginal: null,
  meshEditOriginalProfile: null,
  meshEditProfile: null,
  meshEditUndoStack: [],
  editTool: loadSavedString<EditTool>('clayforge.editTool', 'handles', ['handles', 'pen', 'sculpt', 'crossSection', 'pattern']),
  penEditUndoStack: [],
  refPhotoUrl: null,
  refPhotoOpacity: loadSavedNumber('clayforge.refPhotoOpacity', 0.4, 0, 1),
  refPhotoScale: loadSavedNumber('clayforge.refPhotoScale', 1, 0.01, 100),
  showDimensions: localStorage.getItem('clayforge.showDimensions') !== '0',
  penPattern: null,
  studioTarget: loadSavedString<StudioTarget>('clayforge.studioTarget', 'vase', ['vase', 'mesh']),
  paramGraph: { nodes: [], wires: [] },
  focusedParam: null,
  paramPattern: null
});

export function persistSettings(): void {
  try {
    localStorage.setItem('clayforge.settings', JSON.stringify(app.settings));
    localStorage.setItem('clayforge.printer', app.printer.id);
    localStorage.setItem('clayforge.lang', app.lang);
    localStorage.setItem('clayforge.theme', app.theme);
    // Persist new state too. texture is excluded — too large + transient.
    const { texture, ...vasePersistable } = app.vaseParams;
    void texture;
    localStorage.setItem('clayforge.vase', JSON.stringify(vasePersistable));
    localStorage.setItem('clayforge.brush', JSON.stringify(app.brush));
    localStorage.setItem('clayforge.textureRes', String(app.textureResolution));
    localStorage.setItem('clayforge.cameraView', app.cameraView);
    localStorage.setItem('clayforge.displayMode', app.displayMode);
    localStorage.setItem('clayforge.lightOn', app.lightOn ? '1' : '0');
    localStorage.setItem('clayforge.edgeOn', app.edgeOn ? '1' : '0');
    localStorage.setItem('clayforge.telemetry', app.telemetryOptIn ? '1' : '0');
    localStorage.setItem('clayforge.vaseEditSens', String(app.vaseEditSensitivity));
    localStorage.setItem('clayforge.editTool', app.editTool);
    localStorage.setItem('clayforge.refPhotoOpacity', String(app.refPhotoOpacity));
    localStorage.setItem('clayforge.refPhotoScale', String(app.refPhotoScale));
    localStorage.setItem('clayforge.showDimensions', app.showDimensions ? '1' : '0');
    localStorage.setItem('clayforge.studioTarget', app.studioTarget);
  } catch { /* localStorage may be full or disabled */ }
}

/** True when an STL/imported mesh is loaded and we are NOT in vase mode. */
export function isMeshEditTarget(): boolean {
  return app.triangles.length > 0 && !app.showVasePanel;
}

/** Snapshot the editable params and enter profile-edit mode (handles + wireframe).
 *  Branches: vase mode → snapshots vaseParams; mesh mode → snapshots triangles + extracts profile. */
export function startVaseEdit(): void {
  if (app.showVasePanel) {
    const { texture, ...rest } = app.vaseParams;
    void texture;
    app.vaseEditSnapshot = JSON.parse(JSON.stringify(rest));
  } else if (app.triangles.length > 0) {
    app.meshEditOriginal = app.triangles.slice();
    const profile = extractMeshProfile(app.triangles, 5);
    app.meshEditOriginalProfile = profile.map((p) => ({ ...p }));
    app.meshEditProfile = profile.map((p) => ({ ...p }));
  }
  app.vaseEditUndoStack = [];
  app.meshEditUndoStack = [];
  app.vaseEditingProfile = true;
}

/** Push the current params onto the undo stack (called before each modification). */
export function pushVaseEditUndo(): void {
  if (app.showVasePanel) {
    const { texture, ...rest } = app.vaseParams;
    void texture;
    app.vaseEditUndoStack = [...app.vaseEditUndoStack, JSON.parse(JSON.stringify(rest))].slice(-25);
  } else if (app.meshEditProfile) {
    app.meshEditUndoStack = [...app.meshEditUndoStack, app.meshEditProfile.map((p) => ({ ...p }))].slice(-25);
  }
}

/** Pop the most recent snapshot off the undo stack and apply it. */
export function vaseEditUndo(): void {
  if (app.showVasePanel) {
    if (app.vaseEditUndoStack.length === 0) return;
    const restoreTo = app.vaseEditUndoStack[app.vaseEditUndoStack.length - 1];
    app.vaseParams = { ...app.vaseParams, ...restoreTo };
    app.vaseEditUndoStack = app.vaseEditUndoStack.slice(0, -1);
  } else {
    if (app.meshEditUndoStack.length === 0) return;
    const restoreTo = app.meshEditUndoStack[app.meshEditUndoStack.length - 1];
    app.meshEditProfile = restoreTo.map((p) => ({ ...p }));
    app.meshEditUndoStack = app.meshEditUndoStack.slice(0, -1);
  }
}

/** True when at least one undo step is available in the current mode. */
export function vaseEditCanUndo(): boolean {
  return app.showVasePanel ? app.vaseEditUndoStack.length > 0 : app.meshEditUndoStack.length > 0;
}

/** Exit profile-edit mode keeping current edits. Mesh rebuilds at full quality via $effect. */
export function commitVaseEdit(): void {
  app.vaseEditSnapshot = null;
  app.meshEditOriginal = null;
  app.meshEditOriginalProfile = null;
  app.meshEditProfile = null;
  app.vaseEditUndoStack = [];
  app.meshEditUndoStack = [];
  app.vaseEditingProfile = false;
}

/** Exit profile-edit mode and restore the snapshot taken at startVaseEdit. */
export function cancelVaseEdit(): void {
  if (app.showVasePanel && app.vaseEditSnapshot) {
    app.vaseParams = { ...app.vaseParams, ...app.vaseEditSnapshot };
  } else if (app.meshEditOriginal) {
    app.triangles = app.meshEditOriginal;
  }
  commitVaseEdit();
}

/** Push the current penProfile onto the pen-edit undo stack. Bounded to 25. */
export function pushPenEditUndo(): void {
  const cur = app.vaseParams.penProfile;
  if (!cur) return;
  const snap: PenPath = JSON.parse(JSON.stringify(cur));
  app.penEditUndoStack = [...app.penEditUndoStack, snap].slice(-25);
}

/** Pop and apply the most recent pen-edit snapshot. */
export function penEditUndo(): void {
  if (app.penEditUndoStack.length === 0) return;
  const restoreTo = app.penEditUndoStack[app.penEditUndoStack.length - 1];
  app.vaseParams = { ...app.vaseParams, penProfile: restoreTo };
  app.penEditUndoStack = app.penEditUndoStack.slice(0, -1);
}

export function penEditCanUndo(): boolean {
  return app.penEditUndoStack.length > 0;
}

export function applyProject(p: {
  triangles: AppState['triangles'];
  vaseParams: Partial<AppState['vaseParams']>;
  brush: AppState['brush'];
  settings: AppState['settings'];
  printerId: string;
  textureResolution: number;
  cameraView: AppState['cameraView'];
  lang: AppState['lang'];
  theme: AppState['theme'];
  fileName: string;
  paramGraph?: AppState['paramGraph'];
}): void {
  const profile = BUILTIN_PROFILES.find((x) => x.id === p.printerId);
  if (profile) app.printer = profile;
  app.settings = { ...DEFAULT_SETTINGS, ...p.settings };
  app.vaseParams = { ...DEFAULT_VASE, ...p.vaseParams, texture: null };
  app.brush = { ...app.brush, ...p.brush };
  app.textureResolution = p.textureResolution;
  app.cameraView = p.cameraView;
  app.lang = p.lang;
  app.theme = p.theme;
  app.fileName = p.fileName;
  app.triangles = p.triangles;
  if (p.paramGraph) app.paramGraph = p.paramGraph;
  app.sliceResult = null;
  app.gcodeOutput = null;
  app.viewMode = 'mesh';
  app.mode = 'edit';
  app.status = 'ready';
}
